import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Checkbox,
    TextField, MenuItem, Select, FormControl, InputLabel,
    Stack, CircularProgress, Alert, TableSortLabel, Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';
import { useOutletContext } from 'react-router-dom';

const colunas = [
    { id: 'data',               label: 'Data',                 width: 65  },
    { id: 'caixa',              label: 'Caixa',                width: 55  },
    { id: 'funcionario',        label: 'Funcionário',          width: 120 },
    { id: 'hora_abertura',      label: 'Hora Abertura',        width: 50  },
    { id: 'hora_fechamento',    label: 'Hora Fechamento',      width: 60  },
    { id: 'valor_abertura',     label: 'Valor Abertura',       width: 70  },
    { id: 'valor_fechamento',   label: 'Valor Fechamento',     width: 70  },
    { id: 'diferenca',          label: 'Diferença Turno',      width: 70  },
    { id: 'novo_fechamento',    label: 'Novo Fechamento',      width: 70  },
    { id: 'nova_diferenca',     label: 'Nova Diferença',       width: 70  },
    { id: 'fechamento_abertura',label: 'Fechamento / Abertura',width: 70  },
    { id: 'diferenca_final',    label: 'Diferença Final',      width: 70  },
    { id: 'quem_fechou',        label: 'Quem Fechou',          width: 80  },
    { id: 'observacao',         label: 'Observação',           width: 800 },
    { id: 'scan',               label: 'Scan',                 width: 40  },
];

function ordenar(dados, coluna, direcao) {
    if (!coluna) return dados;
    return [...dados].sort((a, b) => {
        let valA = a[coluna];
        let valB = b[coluna];

        // Nulos sempre vão pro fim
        if (valA == null) return 1;
        if (valB == null) return -1;

        // Data no formato dd/mm/yyyy — converte para comparar corretamente
        if (coluna === 'data') {
            const toDate = (s) => {
                const [d, m, y] = s.split('/');
                return new Date(`${y}-${m}-${d}`);
            };
            valA = toDate(valA);
            valB = toDate(valB);
        }

        if (valA < valB) return direcao === 'asc' ? -1 : 1;
        if (valA > valB) return direcao === 'asc' ? 1 : -1;
        return 0;
    });
}

export default function CaixaTabela() {
    const { handleEditar } = useOutletContext();
    const [dados, setDados] = useState([]);
    const [listaFuncionarios, setListaFuncionarios] = useState([]);
    const [listaCaixas, setListaCaixas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const [ordenacaoColuna, setOrdenacaoColuna] = useState('data');
    const [ordenacaoDirecao, setOrdenacaoDirecao] = useState('desc');

    const mesAtual = new Date().toISOString().slice(0, 7);
    const [filtroMes, setFiltroMes] = useState(mesAtual);
    const [filtroCaixa, setFiltroCaixa] = useState('');
    const [filtroFuncionario, setFiltroFuncionario] = useState('');

    const { authenticatedFetch } = useAuth();

    const handleOrdenar = (coluna) => {
        setOrdenacaoDirecao(prev =>
            ordenacaoColuna === coluna && prev === 'asc' ? 'desc' : 'asc'
        );
        setOrdenacaoColuna(coluna);
    };

    const dadosOrdenados = ordenar(dados, ordenacaoColuna, ordenacaoDirecao);

    const [isRecalculando, setIsRecalculando] = useState(false);

    const fileExportRef = useRef(null);

    const handleExportar = async (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append('file', selectedFile);
        if (filtroMes) formData.append('mes', filtroMes);
        if (filtroCaixa) formData.append('caixa', filtroCaixa);
        if (filtroFuncionario) formData.append('funcionario', filtroFuncionario);

        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/caixa-exportar/`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) throw new Error('Erro ao exportar.');

            // Download automático
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'caixa_exportado.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Erro: ' + err.message);
        }

        // Limpa o input para permitir selecionar o mesmo arquivo novamente
        e.target.value = '';
    };

    const handleRecalcular = async () => {
        setIsRecalculando(true);
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/caixa-recalcular/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mes: filtroMes || null,
                    caixa: filtroCaixa || null,
                    funcionario: filtroFuncionario || null,
                }),
            });
            if (!response.ok) throw new Error('Erro ao recalcular.');
            const data = await response.json();
            alert(`${data.atualizados} registros recalculados.`);
            buscarDados(); // recarrega a tabela
        } catch (err) {
            alert('Erro: ' + err.message);
        } finally {
            setIsRecalculando(false);
        }
    };

    const handleDeletar = async (id) => {
        if (!window.confirm('Tem certeza que deseja apagar este registro?')) return;

        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/caixa/deletar/${id}/`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Erro ao apagar.');
            buscarDados(); // recarrega a tabela
        } catch (err) {
            alert('Erro: ' + err.message);
        }
    };

    const buscarDados = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (filtroMes) params.append('mes', filtroMes);
            if (filtroCaixa) params.append('caixa', filtroCaixa);
            if (filtroFuncionario) params.append('funcionario', filtroFuncionario);

            const response = await authenticatedFetch(`${API_BASE_URL}/api/caixa-listar/?${params}`);
            if (!response.ok) throw new Error('Erro ao buscar dados.');
            setDados(await response.json());
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [filtroMes, filtroCaixa, filtroFuncionario]);

    useEffect(() => {
        const buscarFiltros = async () => {
            try {
                const [resFuncs, resCaixas] = await Promise.all([
                    authenticatedFetch(`${API_BASE_URL}/api/caixa-funcionarios/`),
                    authenticatedFetch(`${API_BASE_URL}/api/caixa-numeros/`),
                ]);
                if (resFuncs.ok) setListaFuncionarios(await resFuncs.json());
                if (resCaixas.ok) setListaCaixas(await resCaixas.json());
            } catch {}
        };
        buscarFiltros();
    }, []);

    useEffect(() => { buscarDados(); }, [buscarDados]);

    const handleToggleScan = (id, novoStatus) => {
        setDados(prev => prev.map(item => item.id === id ? { ...item, scan: novoStatus } : item));
    };

    const fmt = (val) => val != null ? `R$ ${Number(val).toFixed(2)}` : '-';

    return (
        <Box sx={{ width: 1200, p: 2 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
                <TextField
                    label="Mês" type="month" size="small"
                    value={filtroMes} onChange={(e) => setFiltroMes(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
                />
                <FormControl size="small" sx={{ width: 140 }}>
                    <InputLabel>Caixa</InputLabel>
                    <Select label="Caixa" value={filtroCaixa} onChange={(e) => setFiltroCaixa(e.target.value)}>
                        <MenuItem value="">Todos</MenuItem>
                        {listaCaixas.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ width: 200 }}>
                    <InputLabel>Funcionário</InputLabel>
                    <Select label="Funcionário" value={filtroFuncionario} onChange={(e) => setFiltroFuncionario(e.target.value)}>
                        <MenuItem value="">Todos</MenuItem>
                        {listaFuncionarios.map(f => <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>)}
                    </Select>
                </FormControl>
                <Button 
                  variant="outlined"
                  size="small"
                  onClick={handleRecalcular}
                  disabled={isRecalculando}
                  startIcon={isRecalculando ? <CircularProgress size={14} /> : null}
                >
                  {isRecalculando ? 'Recalculando...' : 'Recalcular'}
              </Button>
              <input
                    type="file"
                    accept=".xlsx"
                    hidden
                    ref={fileExportRef}
                    onChange={handleExportar}
                />
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => fileExportRef.current.click()}
                >
                    Exportar XLSX
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {isLoading && <CircularProgress size={24} sx={{ mb: 2 }} />}

            <TableContainer component={Paper} sx={{ maxHeight: '75vh', border: '1px solid #e0e0e0' }}>
                <Table
                    stickyHeader size="small"
                    sx={{
                        tableLayout: 'fixed',
                        "& .MuiTableCell-root": { paddingX: '6px', fontSize: '0.75rem', borderRight: '1px solid #f0f0f0' }
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {colunas.map((col) => (
                                <TableCell
                                    key={col.id}
                                    sx={{ width: col.width, fontWeight: 'bold', bgcolor: '#f5f5f5', whiteSpace: 'normal', lineHeight: 1.1 }}
                                    align={col.id === 'scan' ? 'center' : 'left'}
                                    sortDirection={ordenacaoColuna === col.id ? ordenacaoDirecao : false}
                                >
                                    <TableSortLabel
                                        active={ordenacaoColuna === col.id}
                                        direction={ordenacaoColuna === col.id ? ordenacaoDirecao : 'asc'}
                                        onClick={() => handleOrdenar(col.id)}
                                    >
                                        {col.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            <TableCell sx={{
                                width: 50, fontWeight: 'bold', bgcolor: '#f5f5f5',
                                position: 'sticky', right: 0, zIndex: 100, borderLeft: '2px solid #e0e0e0'
                            }} align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dadosOrdenados.map((row) => (
                            <TableRow key={row.id} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.data}</TableCell>
                                <TableCell>{row.caixa}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.funcionario}>
                                    {row.funcionario}
                                </TableCell>
                                <TableCell>{row.hora_abertura}</TableCell>
                                <TableCell>{row.hora_fechamento}</TableCell>
                                <TableCell>{fmt(row.valor_abertura)}</TableCell>
                                <TableCell>{fmt(row.valor_fechamento)}</TableCell>
                                <TableCell sx={{ color: row.diferenca < 0 ? 'red' : 'inherit' }}>{fmt(row.diferenca)}</TableCell>
                                <TableCell>{fmt(row.novo_fechamento)}</TableCell>
                                <TableCell sx={{ color: row.nova_diferenca < 0 ? 'red' : 'inherit' }}>{fmt(row.nova_diferenca)}</TableCell>
                                <TableCell sx={{ color: row.fechamento_abertura < 0 ? 'red' : 'green', fontWeight: 500 }}>{fmt(row.fechamento_abertura)}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: row.diferenca_final < 0 ? 'red' : 'blue' }}>{fmt(row.diferenca_final)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.quem_fechou}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.observacao}>
                                    {row.observacao}
                                </TableCell>
                                <TableCell align="center" sx={{ paddingX: '1px' }}>
                                    <Checkbox
                                        size="small" checked={row.scan}
                                        onChange={(e) => handleToggleScan(row.id, e.target.checked)}
                                        sx={{ padding: 0 }}
                                    />
                                </TableCell>
                                <TableCell align="center" sx={{
                                    position: 'sticky', right: 0, bgcolor: 'white',
                                    zIndex: 10, borderLeft: '2px solid #e0e0e0'
                                }}>
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                        <IconButton size="small" color="primary" onClick={() => handleEditar(row)} ><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeletar(row.id)}>
                                          <DeleteIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}